export const FACE_COMPARISON_STORAGE_PREFIX = 'face-comparison:';
const FACE_COMPARISON_TTL_MS = 5 * 60 * 1000;

export interface FaceComparisonPayload {
  profileName: string;
  employeeId?: string;
  badgeId?: string;
  profileType?: string;
  alarmType?: string;
  gender?: string;
  timestamp?: number;
  cameraName?: string;
  referencePrimaryUrl?: string;
  referenceFullBodyUrl?: string | null;
  frameUrl?: string;
  faceBox?: number[];
  personBox?: number[];
}

interface StoredEntry {
  createdAt: number;
  payload: FaceComparisonPayload;
}

function generateId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function purgeStaleEntries(): void {
  try {
    const now = Date.now();
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(FACE_COMPARISON_STORAGE_PREFIX)) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        window.localStorage.removeItem(key);
        continue;
      }
      try {
        const entry = JSON.parse(raw) as StoredEntry;
        if (
          !entry?.createdAt ||
          now - entry.createdAt > FACE_COMPARISON_TTL_MS
        ) {
          window.localStorage.removeItem(key);
        }
      } catch {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore storage access issues
  }
}

export function openFaceComparisonTab(payload: FaceComparisonPayload): void {
  purgeStaleEntries();
  const id = generateId();
  try {
    const entry: StoredEntry = { createdAt: Date.now(), payload };
    window.localStorage.setItem(
      `${FACE_COMPARISON_STORAGE_PREFIX}${id}`,
      JSON.stringify(entry)
    );
  } catch {
    return;
  }
  const width = Math.min(600, window.screen.availWidth);
  const height = Math.min(800, window.screen.availHeight);
  const left = Math.max(0, (window.screen.availWidth - width) / 2);
  const top = Math.max(0, (window.screen.availHeight - height) / 2);
  window.open(
    `/shared/face-comparison?id=${encodeURIComponent(id)}`,
    '_blank',
    `popup=yes,noopener,noreferrer,width=${width},height=${height},left=${left},top=${top}`
  );
}

export function readFaceComparisonPayload(
  id: string
): FaceComparisonPayload | null {
  try {
    const key = `${FACE_COMPARISON_STORAGE_PREFIX}${id}`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as StoredEntry;
    if (!entry?.payload) return null;
    if (
      entry.createdAt &&
      Date.now() - entry.createdAt > FACE_COMPARISON_TTL_MS
    ) {
      window.localStorage.removeItem(key);
      return null;
    }
    return entry.payload;
  } catch {
    return null;
  }
}
